// Nord Hydrogen: commerce + restrained motion. No dependencies.
(() => {
  const root = document.documentElement;
  root.classList.remove("no-js");
  const reduced = matchMedia("(prefers-reduced-motion: reduce)").matches;
  const finePointer = matchMedia("(hover: hover) and (pointer: fine)").matches;
  const $ = (s, el = document) => el.querySelector(s);
  const $$ = (s, el = document) => Array.from(el.querySelectorAll(s));
  const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
  const T = (x) => (window.NORD_T && window.NORD_T[x]) || x; // UI strings, translated by the Shopify layout

  // Shopify passes its money format (e.g. "€{{amount}}", "{{amount_no_decimals}} kr"); static build falls back to euros.
  const fmt = (n) => fmtRaw(n).replace(/ /g, "\u00a0"); // prices never break across lines
  const fmtRaw = (n) => {
    const f = window.NORD_MONEY;
    if (!f) return "€" + n.toLocaleString("en-US");
    const two = n.toFixed(2), noDec = Math.round(n).toLocaleString("en-US");
    return f.replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) =>
      k === "amount_no_decimals" ? noDec
      : k === "amount_with_comma_separator" ? n.toLocaleString("de-DE", { minimumFractionDigits: Number.isInteger(n) ? 0 : 2, maximumFractionDigits: 2 })
      : k === "amount_no_decimals_with_comma_separator" ? Math.round(n).toLocaleString("de-DE")
      : k === "amount_no_decimals_with_space_separator" ? Math.round(n).toLocaleString("sv-SE")
      : k === "amount_with_space_separator" ? n.toLocaleString("sv-SE", { minimumFractionDigits: Number.isInteger(n) ? 0 : 2, maximumFractionDigits: 2 })
      : Number.isInteger(n) ? noDec : Number(two).toLocaleString("en-US", { minimumFractionDigits: 2 }));
  };

  /* ---------- Shopify cart permalinks ---------- */
  // Change SHOP when checkout moves to its own subdomain (e.g. https://shop.nordhydrogen.com).
  // Inside the Shopify theme the layout sets window.NORD_SHOP = "" so cart links stay on the current market domain.
  const SHOP = window.NORD_SHOP ?? "https://nordhydrogen.com";
  const VARIANTS = {
    blue: { id: "49158148424019", name: "Deep Space Blue" },
    black: { id: "49158148456787", name: "Horizon Black" },
    red: { id: "50930974785875", name: "Mars Redshift" },
    inhale: { id: "50183176782163", name: "Nord Inhale 2.0" },
    zerowater: { id: "50202312180051", name: "ZeroWater Starter Kit" },
  };
  const cartUrl = (k, q = 1) => `${SHOP}/cart/${VARIANTS[k].id}:${q}`;
  $$("[data-buy]").forEach((a) => (a.href = cartUrl(a.dataset.buy || "blue")));
  // In the Shopify theme: add to the real cart (keeps existing items, fires add_to_cart), then slide in the cart drawer.
  // The permalink href stays as the no-JS / network-failure fallback.
  if (window.NORD_SHOP === "") {
    document.addEventListener("click", async (e) => {
      const a = e.target.closest("a[data-buy]");
      if (!a || e.metaKey || e.ctrlKey) return;
      if (a.hasAttribute("data-soldout")) { e.preventDefault(); return; }
      const m = a.getAttribute("href").match(/\/cart\/([\d:,]+)/);
      if (!m) return;
      e.preventDefault();
      const items = m[1].split(",").map((x) => { const [id, q] = x.split(":"); return { id: +id, quantity: +(q || 1) }; });
      const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || "/";
      a.setAttribute("aria-busy", "true");
      try {
        const r = await fetch(root + "cart/add.js", { method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json" }, body: JSON.stringify({ items }) });
        if (r.ok) { openCart(true); return; }
        const err = await r.json().catch(() => ({}));
        let note = a.parentElement.querySelector(".buy-err");
        if (!note) { note = document.createElement("p"); note.className = "buy-err"; note.setAttribute("role", "alert"); a.after(note); }
        note.textContent = err.description || T("Sorry, that's sold out right now.");
      } catch { location.href = a.href; }
      finally { a.removeAttribute("aria-busy"); }
    });
  }

  /* ---------- cart drawer (Shopify theme only) ---------- */
  // Slides in after add to cart and from the header bag. Talks to the Ajax Cart API; automatic bundle
  // discounts (2 = -10%, 3 = -15%, 4+ = -20%) come back from Shopify already applied to the lines.
  const cartN = $("[data-cart-count]");
  const BOTTLES = ["blue", "black", "red"].map((k) => +VARIANTS[k].id);
  const money = (cents) => fmt(cents / 100);
  const esc = (t) => String(t).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]);
  const setCount = (n) => { if (!cartN) return; cartN.textContent = n; cartN.hidden = !n; };
  let cd = null, cdLast = null;
  const cartRoot = () => (window.Shopify && Shopify.routes && Shopify.routes.root) || "/";
  const cartApi = (path, body) => fetch(cartRoot() + path, body ? { method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json" }, body: JSON.stringify(body) } : { headers: { Accept: "application/json" } }).then((r) => r.json());
  const buildCart = () => {
    cd = document.createElement("div");
    cd.className = "cd"; cd.hidden = true;
    cd.innerHTML = `<div class="cd-scrim" data-cd-close></div>
      <aside class="cd-panel" role="dialog" aria-modal="true" aria-labelledby="cd-h" tabindex="-1">
        <header class="cd-head"><h2 id="cd-h">${T("Your cart")}</h2><span class="cd-n mono" data-cd-n></span><button type="button" class="cd-x" data-cd-close aria-label="${T("Close cart")}"><svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg></button></header>
        <p class="cd-added" data-cd-added hidden role="status"></p>
        <div class="cd-body" data-cd-body aria-live="polite"></div>
        <footer class="cd-foot" data-cd-foot></footer>
      </aside>`;
    document.body.appendChild(cd);
    cd.addEventListener("click", async (e) => {
      if (e.target.closest("[data-cd-close]")) { closeCart(); return; }
      const b = e.target.closest("[data-cd-q]");
      if (b) {
        const line = b.closest("[data-key]");
        line.setAttribute("aria-busy", "true");
        const c = await cartApi("cart/change.js", { id: line.dataset.key, quantity: Math.max(0, +line.dataset.q + +b.dataset.cdQ) }).catch(() => null);
        if (c) renderCart(c); else line.removeAttribute("aria-busy");
        return;
      }
      const more = e.target.closest("[data-cd-more]");
      if (more) {
        more.setAttribute("aria-busy", "true");
        await cartApi("cart/add.js", { items: [{ id: +more.dataset.cdMore, quantity: 1 }] }).catch(() => null);
        renderCart(await cartApi("cart.js"));
      }
    });
    cd.addEventListener("keydown", (e) => {
      if (e.key === "Escape") closeCart();
      if (e.key !== "Tab") return;
      const f = $$("a[href], button:not([disabled])", cd).filter((x) => x.offsetParent !== null);
      if (!f.length) return;
      if (e.shiftKey && document.activeElement === f[0]) { e.preventDefault(); f[f.length - 1].focus(); }
      else if (!e.shiftKey && document.activeElement === f[f.length - 1]) { e.preventDefault(); f[0].focus(); }
    });
  };
  const renderCart = (c) => {
    setCount(c.item_count);
    $("[data-cd-n]", cd).textContent = c.item_count ? `${c.item_count} ${c.item_count > 1 ? T("items") : T("item")}` : "";
    const body = $("[data-cd-body]", cd), foot = $("[data-cd-foot]", cd);
    if (!c.item_count) {
      body.innerHTML = `<div class="cd-empty"><p>${T("Your cart is empty.")}</p><a class="btn btn-atc" href="${cartRoot()}products/nord-vateflaska">${T("Shop the bottle")}</a></div>`;
      foot.innerHTML = ""; return;
    }
    body.innerHTML = `<ul class="cd-lines">${c.items.map((it) => {
      const img = it.image ? it.image + (it.image.includes("?") ? "&" : "?") + "width=160" : "";
      const was = it.original_line_price > it.final_line_price ? `<s>${money(it.original_line_price)}</s>` : "";
      const variant = it.variant_title && !/default/i.test(it.variant_title) ? `<span class="cd-var">${esc(it.variant_title)}</span>` : "";
      return `<li class="cd-line" data-key="${esc(it.key)}" data-q="${it.quantity}">
        ${img ? `<img src="${esc(img)}" alt="" width="72" height="72" loading="lazy">` : `<span class="cd-ph"></span>`}
        <div class="cd-info"><b>${esc(it.product_title)}</b>${variant}
          <div class="cd-qty"><button type="button" data-cd-q="-1" aria-label="${T("Decrease quantity")}">−</button><output>${it.quantity}</output><button type="button" data-cd-q="1" aria-label="${T("Increase quantity")}">+</button></div></div>
        <div class="cd-price">${was}<span>${money(it.final_line_price)}</span></div>
      </li>`;
    }).join("")}</ul>`;
    // bundle nudge: one more bottle unlocks the next tier (up to 4+ = -20%)
    const bottleLines = c.items.filter((it) => BOTTLES.includes(it.variant_id));
    const n = bottleLines.reduce((a, it) => a + it.quantity, 0);
    const next = n >= 4 ? 0 : n === 3 ? 20 : n === 2 ? 15 : n === 1 ? 10 : 0;
    if (next) {
      const last = bottleLines[bottleLines.length - 1];
      body.insertAdjacentHTML("beforeend", `<div class="cd-nudge"><p>${T("Add one more bottle and save {p} on every bottle.").replace("{p}", next + "%")}</p><button type="button" class="btn btn-glass btn-sm" data-cd-more="${last.variant_id}">+ ${esc(last.variant_title || T("bottle"))}</button></div>`);
    }
    const saved = c.items.reduce((a, it) => a + (it.original_line_price - it.final_line_price), 0) + (c.cart_level_discount_applications || []).reduce((a, d) => a + d.total_allocated_amount, 0);
    foot.innerHTML = `${saved ? `<div class="cd-row cd-save"><span>${T("You save")}</span><span>−${money(saved)}</span></div>` : ""}
      <div class="cd-row cd-total"><span>${T("Total")}</span><b>${money(c.total_price)}</b></div>
      <p class="cd-note">${T("Free shipping. Your final total is confirmed at checkout.")}</p>
      <a class="btn btn-atc cd-go" href="${cartRoot()}checkout">${T("Checkout")} <span class="dot">${ARROW_SVG}</span></a>
      <button type="button" class="cd-keep" data-cd-close>${T("Continue shopping")}</button>`;
  };
  const ARROW_SVG = '<svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const openCart = async (added) => {
    if (!cd) buildCart();
    cdLast = document.activeElement;
    const c = await cartApi("cart.js").catch(() => null);
    if (!c) { location.href = cartRoot() + "cart"; return; }
    renderCart(c);
    const note = $("[data-cd-added]", cd);
    note.hidden = !added; if (added) note.textContent = T("Added to your cart");
    cd.hidden = false;
    requestAnimationFrame(() => requestAnimationFrame(() => cd.classList.add("is-open")));
    document.documentElement.classList.add("cd-lock");
    $(".cd-panel", cd).focus({ preventScroll: true });
  };
  const closeCart = () => {
    if (!cd || cd.hidden) return;
    cd.classList.remove("is-open");
    document.documentElement.classList.remove("cd-lock");
    setTimeout(() => { cd.hidden = true; }, reduced ? 0 : 420);
    if (cdLast && cdLast.focus) cdLast.focus({ preventScroll: true });
  };
  if (window.NORD_SHOP === "") {
    cartApi("cart.js").then((c) => setCount(c.item_count)).catch(() => {});
    document.addEventListener("click", (e) => {
      const bag = e.target.closest(".nav-cart");
      if (!bag || e.metaKey || e.ctrlKey) return;
      e.preventDefault(); openCart(false);
    });
  }

  /* ---------- header: announce bar tucks away, header hides on scroll down ---------- */
  const header = $(".site-header");
  let lastY = scrollY, ticking = false;
  const onScroll = () => {
    const y = scrollY;
    document.body.classList.toggle("scrolled", y > 20);
    if (header) {
      if (y > 240 && y > lastY + 4 && !$(".nav-links.open")) header.classList.add("hide");
      else if (y < lastY - 4) header.classList.remove("hide");
    }
    lastY = y; ticking = false;
  };
  addEventListener("scroll", () => { if (!ticking) { ticking = true; requestAnimationFrame(onScroll); } }, { passive: true });

  const menuBtn = $(".menu-btn"), links = $(".nav-links");
  const setMenu = (open) => {
    links.classList.toggle("open", open);
    menuBtn.setAttribute("aria-expanded", open);
    const lbl = $(".menu-lbl", menuBtn); if (lbl) lbl.textContent = open ? T("Close") : T("Menu");
    root.classList.toggle("nav-lock", open);
  };
  if (menuBtn && links) {
    menuBtn.addEventListener("click", () => setMenu(!links.classList.contains("open")));
    links.addEventListener("click", (e) => { if (e.target.closest("a")) setMenu(false); });
    addEventListener("keydown", (e) => { if (e.key === "Escape" && links.classList.contains("open")) { setMenu(false); menuBtn.focus(); } });
    // the Shopify layout drops aria-current; mark the current page from the URL instead
    const here = location.pathname.replace(/\/$/, "");
    $$("a", links).forEach((a) => { const u = new URL(a.href, location.href); if (u.pathname.replace(/\/$/, "") === here && here) a.setAttribute("aria-current", "page"); });
  }


  /* ---------- euro reference amounts in the visitor's currency ---------- */
  // [data-eur="40-60"]: converted with the ratio between the bottle's local price and its €214 list price,
  // so no exchange rate is hard-coded; rounded to whole tens outside the euro.
  const eurEls = $$("[data-eur]"), local = +($("[data-product]")?.dataset.price || 0);
  if (eurEls.length && local && window.NORD_MONEY && (window.Shopify?.currency?.active || "EUR") !== "EUR") {
    const k = local / 214, round = (v) => (v * k >= 100 ? Math.round(v * k / 10) * 10 : Math.round(v * k));
    eurEls.forEach((el) => {
      const [a, b] = el.dataset.eur.split("-").map(Number);
      el.textContent = b ? fmt(round(a)).replace(/[^\d\s\u00a0.,]+/g, "").trim() + "–" + fmt(round(b)) : fmt(round(a));
    });
  }

  /* ---------- article table of contents: highlight the section in view ---------- */
  const toc = $$(".toc a");
  if (toc.length && "IntersectionObserver" in window) {
    const byId = new Map(toc.map((a) => [a.getAttribute("href").slice(1), a]));
    const io2 = new IntersectionObserver((es) => es.forEach((e) => {
      if (!e.isIntersecting) return;
      toc.forEach((a) => a.removeAttribute("aria-current"));
      byId.get(e.target.id)?.setAttribute("aria-current", "true");
    }), { rootMargin: "-20% 0px -70% 0px" });
    byId.forEach((_, id) => { const h = document.getElementById(id); if (h) io2.observe(h); });
  }

  /* ---------- button light follows the pointer (fine pointers only) ---------- */
  if (finePointer) $$(".btn").forEach((b) => b.addEventListener("pointermove", (e) => {
    const r = b.getBoundingClientRect();
    b.style.setProperty("--mx", `${e.clientX - r.left}px`);
    b.style.setProperty("--my", `${e.clientY - r.top}px`);
  }));

  /* ---------- reveal on scroll ---------- */
  if ("IntersectionObserver" in window && !reduced) {
    const io = new IntersectionObserver((es) => es.forEach((e) => {
      if (e.isIntersecting) { e.target.classList.add("in"); io.unobserve(e.target); }
    }), { rootMargin: "0px 0px -6% 0px", threshold: 0.08 });
    $$(".reveal").forEach((el) => io.observe(el));
  } else $$(".reveal").forEach((el) => el.classList.add("in"));

  /* ---------- headline line reveals ---------- */
  if ("IntersectionObserver" in window && !reduced) {
    const lio = new IntersectionObserver((es) => es.forEach((e) => {
      if (e.isIntersecting) { e.target.classList.add("in"); lio.unobserve(e.target); }
    }), { rootMargin: "0px 0px -8% 0px" });
    $$("[data-lines]").forEach((el) => lio.observe(el));
  } else $$("[data-lines]").forEach((el) => el.classList.add("in"));

  /* ---------- count-ups on proof numbers (width locked so units don't jump) ---------- */
  const counters = $$("[data-count]");
  counters.forEach((el) => { const dec = (el.dataset.count.split(".")[1] || "").length; el.textContent = parseFloat(el.dataset.count).toLocaleString(root.lang || "en-US", { minimumFractionDigits: dec, maximumFractionDigits: dec }); });
  if (counters.length && "IntersectionObserver" in window && !reduced) {
    const cio = new IntersectionObserver((es) => es.forEach((e) => {
      if (!e.isIntersecting) return;
      const el = e.target, to = parseFloat(el.dataset.count), dec = (el.dataset.count.split(".")[1] || "").length;
      el.style.minWidth = el.getBoundingClientRect().width + "px"; el.style.display = "inline-block";
      const t0 = performance.now(), dur = 1100;
      const step = (t) => { const k = Math.min(1, (t - t0) / dur), ease = 1 - Math.pow(1 - k, 3); el.textContent = (to * ease).toLocaleString(root.lang || "en-US", { minimumFractionDigits: dec, maximumFractionDigits: dec }); if (k < 1) requestAnimationFrame(step); };
      requestAnimationFrame(step); cio.unobserve(el);
    }), { threshold: 0.6 });
    counters.forEach((c) => cio.observe(c));
  }

  /* ---------- ambient cursor glow: only animates while the pointer moves ---------- */
  const glow = $(".cursor-glow");
  if (glow && finePointer && !reduced) {
    let gx = 0, gy = 0, cx = 0, cy = 0, running = false;
    const loop = () => {
      cx += (gx - cx) * 0.14; cy += (gy - cy) * 0.14;
      glow.style.transform = `translate(${cx}px, ${cy}px)`;
      if (Math.abs(gx - cx) + Math.abs(gy - cy) > 0.5) requestAnimationFrame(loop); else running = false;
    };
    addEventListener("mousemove", (e) => { gx = e.clientX; gy = e.clientY; glow.classList.add("on"); if (!running) { running = true; requestAnimationFrame(loop); } }, { passive: true });
  }

  /* ---------- dose calculator ---------- */
  const calc = $("[data-calc]");
  if (calc) {
    const PER_FILL = { 10: 1.08, 5: 0.72 }, MAX = 6;
    let cycle = 10, fills = 3;
    const num = $(".num", calc), sum = $(".meter-sum", calc), fill = $(".fill", calc), you = $(".you", calc), out = $("output", calc);
    const render = () => {
      const total = PER_FILL[cycle] * fills, p = Math.min(total / MAX, 1);
      num.textContent = total.toLocaleString(root.lang || "en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
      out.textContent = `${fills} ${fills > 1 ? T("bottles") : T("bottle")}`;
      const nf = (v, d) => v.toLocaleString(root.lang || "en-US", { minimumFractionDigits: d, maximumFractionDigits: d });
      sum.textContent = `${fills} × ${nf(PER_FILL[cycle], 2)} mg (${cycle}${T("-min cycle")}) = ${nf(total, 2)} ${T("mg H₂ per day")}`;
      fill.style.setProperty("--p", p);
      you.style.setProperty("--p", p);
      you.textContent = `${T("you")} · ${nf(total, 1)} mg`;
    };
    $$("[data-cycle]", calc).forEach((b) => b.addEventListener("click", () => { cycle = +b.dataset.cycle; $$("[data-cycle]", calc).forEach((x) => x.setAttribute("aria-pressed", x === b)); render(); }));
    $("[data-step='-1']", calc).addEventListener("click", () => { fills = Math.max(1, fills - 1); render(); });
    $("[data-step='1']", calc).addEventListener("click", () => { fills = Math.min(5, fills + 1); render(); });
    render();
  }

  /* ---------- buy box: colour, qty, upsell, sticky bar ---------- */
  const buy = $("[data-product]");
  if (buy) {
    const main = $(".gallery-main .gm-img", buy) || $(".gallery-main img", buy), chosen = $("[data-chosen]", buy);
    const gv = $(".gallery-main .gm-video", buy);
    const sw = $$(".swatch", buy), th = $$(".thumbs button", buy);
    const qOut = $("[data-qty-out]", buy), total = $("[data-total]", buy);
    const sticky = $(".sticky-atc"), sImg = $("[data-sticky-img]"), sName = $("[data-sticky-name]");
    const upsell = $("[data-upsell]", buy);
    const unit = +(buy.dataset.price || 214), zwPrice = +(buy.dataset.zw || 84);
    let variant = buy.dataset.default || "blue", qty = 1, withZW = false;
    const tierBox = $(".tiers", buy), tiers = $$(".tier", buy), saveLine = $("[data-save]", buy);
    tiers.forEach((t) => {
      const n = +t.dataset.tier, d = n >= 4 ? 0.2 : n === 3 ? 0.15 : n === 2 ? 0.1 : 0;
      const pr = t.querySelector("[data-tier-price]"); if (pr) pr.textContent = n > 1 ? `${fmt(unit * (1 - d))} / ${T("bottle")}` : fmt(unit);
      t.addEventListener("click", () => { qty = n; sync(); });
    });
    // Live stock from the Shopify layout ({variantId: true/false}); absent in the static build = all available.
    const AVAIL = window.NORD_AVAIL || {};
    const inStock = (k) => AVAIL[VARIANTS[k]?.id] !== false;
    $$(".swatch", buy).forEach((b) => {
      if (inStock(b.dataset.variant)) return;
      b.classList.add("is-soldout"); b.setAttribute("aria-disabled", "true");
      b.insertAdjacentHTML("beforeend", `<small class="so">${T("Sold out")}</small>`);
    });
    if (!inStock(variant)) { const first = $$(".swatch", buy).find((b) => inStock(b.dataset.variant)); if (first) { variant = first.dataset.variant; $$(".swatch", buy).forEach((b) => b.setAttribute("aria-checked", b === first)); } }

    const sync = () => {
      $$("[data-buy]").forEach((a) => {
        if (!(a.closest("[data-product]") || a.hasAttribute("data-sticky-btn"))) return;
        a.dataset.buy = variant;
        const q = a.hasAttribute("data-sticky-btn") ? 1 : qty;
        a.href = cartUrl(variant, q) + (withZW ? `,${VARIANTS.zerowater.id}:1` : "");
      });
      if (qOut) qOut.textContent = qty;
      $$("[data-express-id]", buy).forEach((i) => (i.value = VARIANTS[variant].id));
      $$("[data-express-qty]", buy).forEach((i) => (i.value = qty));
      // volume tiers (bottle): 2 = -10%, 3 = -15%, 4+ = -20%, mirrored by Shopify automatic discounts at checkout
      const disc = tierBox ? (qty >= 4 ? 0.2 : qty === 3 ? 0.15 : qty === 2 ? 0.1 : 0) : 0;
      if (total) total.textContent = fmt(unit * qty * (1 - disc) + (withZW ? zwPrice : 0));
      if (saveLine) { saveLine.hidden = !disc; if (disc) saveLine.textContent = `${T("You save")} ${fmt(unit * qty * disc)} (−${Math.round(disc * 100)}%)`; }
      if (tierBox) tiers.forEach((t) => t.setAttribute("aria-checked", String(+t.dataset.tier === Math.min(qty, 4))));
      if (chosen) chosen.textContent = VARIANTS[variant].name;
      if (sName) sName.textContent = VARIANTS[variant].name;
      const out = !inStock(variant);
      $$("[data-buy]").forEach((a) => { if (a.closest("[data-product]") || a.hasAttribute("data-sticky-btn")) a.toggleAttribute("data-soldout", out); });
    };
    const markThumb = (key) => th.forEach((t) => t.setAttribute("aria-current", (t.dataset.video || t.dataset.src) === key));
    const playMain = () => { if (gv && !gv.hidden && !reduced) gv.play().catch(() => {}); };
    const showVideo = (src, poster, alt) => {
      if (!gv) return;
      main.hidden = true; gv.hidden = false;
      gv.poster = poster; gv.setAttribute("aria-label", alt);
      if (gv.getAttribute("src") !== src) { gv.src = src; }
      playMain(); markThumb(src);
    };
    const show = async (src, alt) => {
      const next = new Image(); next.src = src;
      try { await next.decode(); } catch (e) {}
      if (gv) { gv.pause(); gv.hidden = true; }
      main.hidden = false;
      main.classList.add("swap"); main.src = src; main.alt = alt;
      requestAnimationFrame(() => requestAnimationFrame(() => main.classList.remove("swap")));
      markThumb(src);
    };
    if (gv) {
      // start the first loop only when the gallery is on screen
      if ("IntersectionObserver" in window) new IntersectionObserver(([e]) => {
        if (e.isIntersecting) { if (!gv.getAttribute("src")) gv.src = gv.dataset.src; playMain(); } else gv.pause();
      }, { threshold: 0.3 }).observe(gv);
      else { gv.src = gv.dataset.src; playMain(); }
    }
    sw.forEach((s) => s.addEventListener("click", () => {
      if (s.classList.contains("is-soldout")) return;
      sw.forEach((x) => x.setAttribute("aria-checked", x === s));
      variant = s.dataset.variant;
      const name = VARIANTS[variant].name;
      const cv = $('[data-role="cvideo"]', buy), cs = $('[data-role="cstill"]', buy);
      if (cs && s.dataset.img) { cs.dataset.src = s.dataset.img; $("img", cs).src = s.dataset.img; $("img", cs).alt = name; }
      if (cv && s.dataset.video) {
        cv.dataset.video = s.dataset.video; cv.dataset.poster = s.dataset.poster;
        $("img", cv).src = s.dataset.poster; $("img", cv).alt = `${name} (video)`;
        showVideo(s.dataset.video, s.dataset.poster, T("Nord Hydrogen Bottle in") + ` ${name}`);
      } else if (s.dataset.img) show(s.dataset.img, T("Nord Hydrogen Bottle in") + ` ${name}`);
      if (sImg && s.dataset.img) sImg.src = s.dataset.img;
      sync();
    }));
    th.forEach((t) => t.addEventListener("click", () => t.dataset.video ? showVideo(t.dataset.video, t.dataset.poster, $("img", t).alt) : show(t.dataset.src, $("img", t).alt)));
    $$("[data-qty]", buy).forEach((b) => b.addEventListener("click", () => { qty = clamp(qty + +b.dataset.qty, 1, 9); sync(); }));
    if (upsell) upsell.addEventListener("click", (e) => { e.preventDefault(); withZW = !withZW; upsell.classList.toggle("on", withZW); upsell.setAttribute("aria-pressed", withZW); sync(); });
    sync();

    const atc = $(".btn-atc", buy);
    if (sticky && atc && "IntersectionObserver" in window) {
      document.body.classList.add("has-sticky");
      const sBtn = $("[data-sticky-btn]", sticky);
      new IntersectionObserver(([e]) => {
        const on = !e.isIntersecting && e.boundingClientRect.top < 0;
        sticky.classList.toggle("show", on);
        sticky.setAttribute("aria-hidden", !on);
        sBtn.tabIndex = on ? 0 : -1;
        document.body.classList.toggle("sticky-on", on);
      }).observe(atc);
    }
  }

  /* ---------- video: preview loops in view, dialog with sound ---------- */
  const dlg = $("dialog.vmodal"), dVideo = dlg && $("video", dlg);
  let opener = null;
  const openVideo = (src, from) => {
    if (!dlg) return;
    opener = from; dVideo.src = src; dlg.showModal();
    $(".vmodal-x", dlg).focus();
    dVideo.muted = false; dVideo.play().catch(() => {});
  };
  if (dlg) {
    dlg.addEventListener("close", () => { dVideo.pause(); dVideo.removeAttribute("src"); dVideo.load(); if (opener) opener.focus(); });
    $(".vmodal-x", dlg).addEventListener("click", () => dlg.close());
    dlg.addEventListener("click", (e) => { if (e.target === dlg) dlg.close(); });
  }
  const tiles = $$(".vt-tile");
  if (tiles.length && "IntersectionObserver" in window && !reduced) {
    const vio = new IntersectionObserver((es) => es.forEach((e) => {
      const v = $("video", e.target);
      if (e.isIntersecting) { if (!v.src) v.src = v.dataset.loop; v.play().catch(() => {}); } else v.pause();
    }), { threshold: 0.4 });
    tiles.forEach((t) => vio.observe(t));
  }
  tiles.forEach((t) => t.addEventListener("click", () => openVideo(t.dataset.full, t)));

  /* floating bubble: desktop only (CSS), after the buy box, dismissable for the session */
  const bubble = $(".vbubble"), buyBox = $("#buy");
  if (bubble && buyBox && "IntersectionObserver" in window) {
    let dismissed = false;
    try { dismissed = sessionStorage.getItem("nh-bubble") === "0"; } catch (e) {}
    const bv = $("video", bubble), bo = $(".vbubble-open", bubble);
    const setShown = (on) => {
      bubble.classList.toggle("show", on);
      $$("button", bubble).forEach((b) => (b.tabIndex = on ? 0 : -1));
      if (on && !bv.src && !reduced) { bv.src = bv.dataset.loop; bv.play().catch(() => {}); }
    };
    setShown(false);
    new IntersectionObserver(([e]) => setShown(!dismissed && !e.isIntersecting && e.boundingClientRect.top < 0)).observe(buyBox);
    $(".vbubble-x", bubble).addEventListener("click", () => { dismissed = true; try { sessionStorage.setItem("nh-bubble", "0"); } catch (e) {} setShown(false); });
    bo.addEventListener("click", () => openVideo(bo.dataset.full, bo));
  }

  /* how it works: the 10-minute ring runs one full cycle every 12 s while on screen; the step cards follow along */
  $$("[data-timer]").forEach((ht) => {
    const v = $("video", ht), arc = $("[data-arc]", ht), clock = $("[data-clock]", ht), steps = $$(".ht-steps li", ht);
    if (reduced) return;
    const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
    let on = false, t0 = 0;
    const tick = (now) => {
      if (!on) return;
      const k = ((now - t0) / 12000) % 1;
      clock.textContent = fmt(600 * (1 - k));
      arc.style.strokeDashoffset = 301.6 * (1 - k);
      const s = k < 0.12 ? 0 : k < 0.9 ? 1 : 2;
      steps.forEach((el, i) => el.classList.toggle("on", i === s));
      requestAnimationFrame(tick);
    };
    new IntersectionObserver(([e]) => {
      on = e.isIntersecting;
      if (on) { if (!v.src) v.src = v.dataset.src; v.play().catch(() => {}); t0 = performance.now(); requestAnimationFrame(tick); }
      else v.pause();
    }, { threshold: 0.25 }).observe(ht);
  });

  /* Nord logo film (About, 404): plays once when on screen and rests on the logo; replay button after */
  $$("[data-brand-film]").forEach((bf) => {
    const v = $("video", bf), again = $(".bf-replay", bf);
    if (reduced) { v.poster = v.dataset.poster; return; }
    new IntersectionObserver(([e], o) => {
      if (!e.isIntersecting) return;
      o.disconnect();
      v.src = innerWidth < 700 ? v.dataset.srcSm : v.dataset.srcLg;
      v.play().catch(() => { v.poster = v.dataset.poster; });
    }, { threshold: 0.3 }).observe(bf);
    v.addEventListener("ended", () => { again.hidden = false; });
    again.addEventListener("click", () => { again.hidden = true; v.currentTime = 0; v.play(); });
  });

  /* click-to-play film */
  $$(".film-player").forEach((fp) => {
    const yt = $("[data-yt]", fp), btn = $(".play-btn", fp);
    if (yt) { // our YouTube film: nothing loads from YouTube until the visitor presses play
      btn.addEventListener("click", () => {
        const f = document.createElement("iframe");
        f.src = `https://www.youtube-nocookie.com/embed/${yt.dataset.yt}?autoplay=1&rel=0&modestbranding=1&playsinline=1`;
        f.title = yt.alt; f.allow = "autoplay; encrypted-media; picture-in-picture; fullscreen"; f.allowFullscreen = true;
        yt.replaceWith(f); fp.classList.add("playing"); f.focus();
      });
      return;
    }
    const v = $("video", fp);
    btn.addEventListener("click", () => {
      if (!v.src) v.src = innerWidth < 700 ? v.dataset.srcSm : v.dataset.srcLg;
      v.controls = true; fp.classList.add("playing"); v.play(); v.focus();
    });
    v.addEventListener("ended", () => { fp.classList.remove("playing"); v.controls = false; btn.focus(); });
  });

  /* reviews: show more */
  // Stacked cards, revealed in steps: 3 at first on phones (6 on larger screens), then 6 more per tap.
  // The button shows how many are left and disappears when all are shown.
  $$("[data-more]").forEach((b) => {
    const g = b.closest("section").querySelector(".rev-grid");
    if (!g) return;
    const label = b.textContent.trim();
    const hidden = () => $$(".rev.more", g);
    const update = () => { const n = hidden().length; if (n) b.textContent = `${label} (${n})`; else b.parentElement.remove(); };
    if (innerWidth < 700) $$(".rev:not(.more)", g).slice(3).forEach((c) => c.classList.add("more"));
    update();
    b.addEventListener("click", () => {
      const next = hidden().slice(0, 6);
      next.forEach((c) => { c.classList.remove("more"); c.classList.add("rev-in"); });
      update();
      if (next[0]) { next[0].tabIndex = -1; next[0].focus({ preventScroll: true }); }
    });
  });

  const yr = $("[data-year]");
  if (yr) yr.textContent = new Date().getFullYear();

  // test hook for the screenshot harness
  const jump = new URLSearchParams(location.search).get("jump");
  addEventListener("load", () => { if (jump !== null) scrollTo(0, +jump || 0); window.__ready = true; });
})();
